import { createSlice } from '@reduxjs/toolkit'
import { ApprovalStatus } from 'api/approval/approval.api'
import PromotionsApi, { PromotionTreatmentProps } from 'api/promotion/promotion.api'
import { PDFPayloadOutStandingPaymentProps } from 'component/Treatments/PopupOutStandingPayment'
import _ from 'lodash'
import { Dispatch } from 'redux'
import { getBranch, getCrm } from 'utils/app.utils'

// orderIndex helpers: ใช้ stamp ลำดับการกดเพิ่มของรายการใน summeryTreatments
// - ปกติ: เริ่มที่ 0 และเพิ่มขึ้นเรื่อยๆ
// - operativeFixCode (auto-injected, ต้องอยู่บนสุด): ใช้ค่า negative
export const FIX_CODE_ORDER_BASE = -1000

export const nextOrderIndex = (items: any[] = []): number => {
  let max = -1
  for (const it of items) {
    const v = it?.orderIndex
    if (typeof v === 'number' && v >= 0 && v > max) max = v
  }
  return max + 1
}

export const assignOrderIndex = (existing: any[] = [], newItems: any[] = []): any[] => {
  let idx = nextOrderIndex(existing)
  return newItems.map((it: any) => ({ ...it, orderIndex: idx++ }))
}

export const assignFixCodeOrderIndex = (items: any[] = []): any[] =>
  items.map((it: any, i: number) => ({ ...it, orderIndex: FIX_CODE_ORDER_BASE + i }))

// Walk a list and stamp orderIndex on any item that doesn't have one yet.
// ใช้สำหรับกรณีที่ list ผสมระหว่าง item เดิม (มี orderIndex) กับ item ใหม่ที่เพิ่งเพิ่ม
export const fillMissingOrderIndex = (list: any[] = []): any[] => {
  let max = -1
  for (const it of list) {
    const v = it?.orderIndex
    if (typeof v === 'number' && v >= 0 && v > max) max = v
  }
  return list.map((it: any) => {
    if (typeof it?.orderIndex === 'number') return it
    max += 1
    return { ...it, orderIndex: max }
  })
}

// Sort merge: รวม operatives + products แล้วเรียงตาม orderIndex
// Fallback: รายการที่ไม่มี orderIndex (legacy) ไปท้ายสุด คงลำดับเดิมไว้ (operative ก่อน product)
export const mergeAndSortByOrderIndex = (operatives: any[] = [], products: any[] = []): any[] => {
  const hasIdx = (v: any) => typeof v?.orderIndex === 'number'
  const withIdx: any[] = []
  const legacyOps: any[] = []
  const legacyProds: any[] = []
  for (const it of operatives) (hasIdx(it) ? withIdx : legacyOps).push(it)
  for (const it of products) (hasIdx(it) ? withIdx : legacyProds).push(it)
  withIdx.sort((a, b) => (a.orderIndex as number) - (b.orderIndex as number))
  return [...withIdx, ...legacyOps, ...legacyProds]
}

export interface SummaryTreatmentsInterface {
  orderIndex?: number
  tempOrderTreatmentId?: number | null
  teeth?: string
  teethJson?: string
  operativeId?: number
  qty?: number
  price?: number
  doctorId?: number
  discount?: number
  discountUnit?: 'BAHT' | 'PERCENT'
  additional?: number
  additionalUnit?: 'BAHT' | 'PERCENT'
  dfAmount?: number
  dfUnit?: 'BAHT' | 'PERCENT'
  dfDiscountAmount?: number
  dfDiscountUnit?: 'BAHT' | 'PERCENT'
  dfAmountNet?: number
  cfAmount?: number
  cfDiscountAmount?: number
  cfDiscountUnit?: 'BAHT' | 'PERCENT'
  cfAmountNet?: number
  dfRemark?: string
  clinicFeeId?: number | null
  total?: number
  note?: string
  noteNextVisit?: string
  refType?: 'COURSE' | 'PLAN' | null
  refId?: number | null
  nextVisit?: NextVisitInterface
}

export interface SummaryProductsInterface {
  orderIndex?: number
  tempOrderProductId: number
  inventoryId: number
  group: 'PRODUCT' | 'MEDICINE' | 'ALL'
  qty: number
  price: number
  doctorId: number
  discount: number
  discountUnit: 'BAHT' | 'PERCENT'
  additional: number
  additionalUnit: 'BAHT' | 'PERCENT'
  note: string
  refType: 'COURSE' | 'PLAN' | null
  refId?: number | null
}

export interface PatientDiagnosisInterface {
  patientDiagnosisId: number
  diagnosisId: number
  doctorId: number
  note: string
}

export interface PatientRecommendsInterface {
  patientRecommendId: number
  recommendId: number
  note: string
  patientRecommends: any
}

export interface TreatmentNextVisitInterface {
  operativeTypeId?: number
  operativeId?: number
}

export interface NextVisitInterface {
  branchId?: number
  refId?: number
  date?: string
  timeStart?: string
  durationMinute?: number
  durationText?: string
  doctorId?: number
  treatment?: any
  practice?: string
  price?: number
  hasLab?: 'HAS' | 'NOT_HAS'
  hasXray?: 'HAS' | 'NOT_HAS'
  remark?: string
  appointmentLabs?: any | null;
}

export interface TreatmentStateInterface {
  date?: string
  importantSymptomsDoctor: string
  teethType: 'ADULT' | 'CHILD' | ''
  toothChart?: any
  servicePointId: number
  summeryTreatments: any
  summeryTreatmentsOld: any
  summeryProducts: SummaryProductsInterface[]
  productsState: ProductStickerType[]
  stickerState: StickerType[]
  patientDiagnosis: any
  patientRecommends: any
  outStandingPayment?: PDFPayloadOutStandingPaymentProps
  overdueStatus?: string
}

export interface TreatmentStateType {
  importantSymptoms?: string
  importantSymptomsDoctor: string
  toothChart?: any
  teethType: 'ADULT' | 'CHILD' | ''
  servicePointId: number
  summeryTreatments?: SummaryTreatmentsInterface[]
  summeryTreatmentsOld?: SummaryTreatmentsInterface[]
  summeryProducts?: SummaryProductsInterface[]
  patientDiagnosis?: PatientDiagnosisInterface[]
  patientRecommends?: PatientRecommendsInterface[]
  productsState?: ProductStickerType[]
  stickerState?: StickerType[],
  overdueTotal?: number,
  overdueDate?: string,
  overdueDateStart?: string,
  patientSignature?: string,
  doctorId?: number,
  doctorName?: string,
  doctorSignature?: string,
  managerName?: string,
  managerSignature?: string
  status?: string,
  requestOverdue?: string
  approvedAt?: string;
  approveItemId?: number
  approvedRemark?: string
  newOutStandingPayment?: any
  concurrencyToken?: string
}

export interface ProductStickerType {
  sticker: string | null
  tempOrderProductId?: number
  doctorId: number
  qty: number
  price: number
  discount: number
  discountUnit: "BAHT"
  additional: number
  additionalUnit: "BAHT"
  total: number
  itemId: number
  patientTreatmentCourseId?: number
  inventoryId?: number
  promotion?: any
}

export interface StickerType {
  logo?: string
  clinicName?: string
  branchAddress?: string
  branchTel?: string
  patientFullName?: string
  itemId?: number
  itemCode?: string
  itemName?: string
  itemNameEn?: string
  expiredDate?: string
  doctorName?: string
  image?: {
    fileId?: number
    filePath?: string
    fileName?: string
  }
  stock?: number
  defaultProductUnitName?: string
  dosage?: number

  //  -- วิธีใช้ยา/ผลิตภัณฑ์
  productInstructionId?: number
  productInstructionName?: string
  productInstructionNameEn?: string

  //  -- หน่วยยา/ผลิตภัณฑ์
  productUnitId?: number
  productUnitName?: string
  productUnitNameEn?: string

  //  -- เวลาใช้ยา/ผลิตภัณฑ์
  productUseId?: number
  productUseDetail?: string
  productUseDetailEn?: string

  //  -- อาการ
  productSymptomId?: number
  productSymptomDetail?: string
  productSymptomDetailEn?: string

  //  -- ข้อควรระวัง
  productWarningId?: number
  productWarningDetail?: string
  productWarningDetailEn?: string

  //  -- คำแนะนำเพิ่มเติม
  itemRecommend?: string

  doctorPrefix?: string
}

const initialState: any = {
  treatment: {
    importantSymptomsDoctor: '',
    teethType: 'ADULT',
    servicePointId: 0,
    summeryTreatments: [],
    summeryTreatmentsOld: [],
    summeryProducts: [],
    productsState: [],
    stickerState: [],
    patientDiagnosis: [],
    patientRecommends: [],
    overdueStatus: ''
  },
  toothChart: [],
  toothChartSelected: [],
  toothChartColor: '',
  historyToothChart: [],
  isSubmitTootChart: false,
  isEditTootChartType: '',
  isEditTootChart: null,
  newStickerProduct: [],
  toothChartPositions: [],
  approvalRight: {},
  promotionTreatments: [],
  isLoading: false,
  concurrencyToken: null as string | null,

}

export const treatmentSlice = createSlice({
  name: 'treatmentState',
  initialState,
  reducers: {
    setTreatment: (state: any, action: any) => {
      state.treatment = action.payload.value
    },
    setTreatmentByKey: (state: any, action: any) => {
      state.treatment[action.payload.key] = action.payload.value
    },
    setSummeryTreatments: (state: any, action: any) => {
      if (!state.treatment.summeryTreatments[action.payload.index][action.payload.key]) state.treatment.summeryTreatments[action.payload.index][action.payload.key] = null
      state.treatment.summeryTreatments[action.payload.index][action.payload.key] = action.payload.value
    },
    setProductsState: (state: any, action: any) => {
      if (!state.treatment.productsState[action.payload.index][action.payload.key]) state.treatment.productsState[action.payload.index][action.payload.key] = null
      state.treatment.productsState[action.payload.index][action.payload.key] = action.payload.value
    },
    setStickerState: (state: any, action: any) => {
      if (!state.treatment.productsState[action.payload.index][action.payload.key]) state.treatment.productsState[action.payload.index][action.payload.key] = null
      state.treatment.stickerState[action.payload.index][action.payload.key] = action.payload.value
    },
    setColorToothChart: (state: any, action: any) => {
      state.toothChartColor = action.payload.value
    },
    setToothChart: (state: any, action: any) => {
      state.toothChart[action.payload.key] = action.payload.value
    },
    setToothChartSelected: (state: any, action: any) => {
      state.toothChartSelected = action.payload
    },
    setTeethPosition: (state: any, action: any) => {
      state.toothChart[action.payload.key].position = action.payload.value
    },
    resetToothChart: (state: any,) => {
      state.toothChart = []
      state.toothChartColor = ''
    },
    setSubmitTootChart: (state: any, action: any) => {
      state.isSubmitTootChart = action.payload
    },
    setEditTootChart: (state: any, action: any) => {
      state.isEditTootChart = action.payload
    },
    setEditTootChartType: (state: any, action: any) => {
      state.isEditTootChartType = action.payload
    },
    setDiagnosisByKey: (state: any, action: any) => {
      state.treatment[action.payload.key] = action.payload.value
    },
    setDiagnosisTreatments: (state: any, action: any) => {
      state.treatment.patientDiagnosis[action.payload.index][action.payload.key] = action.payload.value
    },
    setRecommendByKey: (state: any, action: any) => {
      state.treatment[action.payload.key] = action.payload.value
    },
    setRecommendTreatments: (state: any, action: any) => {
      state.treatment.patientRecommends[action.payload.index] = action.payload.value
    },
    setHistoryToothChart: (state: any, action: any) => {
      state.historyToothChart = action.payload
    },
    setNewStickerProduct: (state: any, action: any) => {
      state.newStickerProduct = action.payload.value
    },
    setToothChartPosition: (state: any, action: any) => {
      state.toothChartPositions = action.payload
    },
    setApprovalRight: (state: any, action: any) => {
      state.approvalRight = action.payload
    },
    setPromotionTreatments: (state: any, action: any) => {
      state.promotionTreatments = action.payload
    },
    setIsLoading: (state: any, action: any) => {
      state.isLoading = action.payload
    },
    setConcurrencyToken: (state: any, action: any) => {
      state.concurrencyToken = action.payload
    },
  }
})

export const treatment = (state: any) => state.treatment.treatment
export const diagnosisObj = (state: any) => state.treatment.treatment
export const RecommendObj = (state: any) => state.treatment.treatment
export const toothChart = (state: any) => state.treatment.toothChart
export const toothChartColor = (state: any) => state.treatment.toothChartColor
export const toothChartSelected = (state: any) => state.treatment.toothChartSelected
export const isSubmitTootChart = (state: any) => state.treatment.isSubmitTootChart
export const isEditTootChart = (state: any) => state.treatment.isEditTootChart
export const isEditTootChartType = (state: any) => state.treatment.isEditTootChartType
export const historyToothChart = (state: any) => state.treatment.historyToothChart
export const newStickerProduct = (state: any) => state.treatment.newStickerProduct
export const toothChartPositions = (state: any) => state.treatment.toothChartPositions
export const checkApprovalRight = (state: any) => state.treatment.approvalRight
export const promotionsTreatments = (state: any) => state.treatment.promotionTreatments
export const treatmentIsLoading = (state: any) => state.treatment.isLoading
export const concurrencyTokenSelector = (state: any) => state.treatment.concurrencyToken


export const { setTreatment, setTreatmentByKey, setToothChart, setColorToothChart, setTeethPosition, resetToothChart, setSummeryTreatments, setProductsState, setStickerState, setDiagnosisByKey, setDiagnosisTreatments, setToothChartSelected, setSubmitTootChart, setEditTootChart, setEditTootChartType, setRecommendByKey, setRecommendTreatments, setHistoryToothChart, setNewStickerProduct, setToothChartPosition, setApprovalRight, setPromotionTreatments, setIsLoading, setConcurrencyToken } = treatmentSlice.actions

export const loadPromotionTreatments = (patientId: number, itemTreatments: any, date?: string) => {
  return async (dispatch: Dispatch) => {
    if (!getCrm()) {
      dispatch(setPromotionTreatments([]))
      return
    }

    const newPayload: PromotionTreatmentProps = {
      patientId: patientId,
      branchId: getBranch(),
      totalPrice: _.sumBy(itemTreatments.map((item: any) => {
        if (item?.approvalItems?.status === ApprovalStatus.PENDING) {
          const newData = JSON.parse(item?.approvalItems?.body)
          return { ...item, total: newData?.total }
        } else {
          return { ...item }
        }
      }), 'total'),
      date: date,
      items: itemTreatments?.filter((item: any) => {
        if (item?.promotion?.length) {
          return item?.promotion?.every((info: any) => info?.promotionType !== "EXTRA")
        } else {
          return item
        }

      }).map((item: any) => {
        if (item?.group && item.type !== "TREATMENT") {
          return {
            itemType: item?.group,
            itemId: item?.itemId || item?.operativeId || 0,
            qty: item?.qty
          }
        } else {
          return {
            itemType: "OPERATIVE",
            itemId: item?.operativeId || 0,
            qty: item?.qty
          }
        }
      })
    }
    PromotionsApi.findPromotionTreatments(newPayload).then((res) => {
      if (res?.data?.length) {
        dispatch(setPromotionTreatments(res?.data))
      } else {
        dispatch(setPromotionTreatments([]))
      }
    })
  }
}


export default treatmentSlice.reducer
